import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';

export function CustomerReviewsSection() {
  return (
    <section className="py-24 bg-gradient-to-b from-secondary to-background">
      <div className="container mx-auto px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-black uppercase mb-4 text-foreground">
            WHAT OUR CLIENTS SAY
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Join hundreds of satisfied businesses who transformed their workspaces with our modular solutions
          </p>
        </div>

        {/* Featured Review */}
        <div className="mb-12 bg-white rounded-2xl shadow-2xl p-8 md:p-12 border-2 border-primary">
          <div className="flex items-center gap-2 mb-6">
            {[...Array(5)].map((_, i) => (
              <svg key={i} className="w-8 h-8 fill-primary" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            ))}
          </div>
          <p className="text-2xl md:text-3xl font-semibold text-foreground mb-6 leading-relaxed italic">
            "Absolutely transformed our office! The modular workstations allowed us to configure spaces perfectly for our growing team. Setup was quick, quality is outstanding, and the flexibility means we can easily adapt as we scale. Best investment we've made in years!"
          </p>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-xl font-black text-foreground">Rajesh Kumar</p>
              <p className="text-sm text-muted-foreground">Tech Startup Founder</p>
            </div>
            <div className="px-4 py-2 bg-primary/10 border border-primary rounded-full">
              <p className="text-sm font-bold text-primary uppercase">Modular Workstations</p>
            </div>
          </div>
        </div>

        {/* Review Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { name: 'Priya Sharma', role: 'Marketing Manager', review: 'The privacy booths are a game-changer! Perfect for video calls and focused work. Our team productivity has increased significantly.', category: 'Privacy Booths' },
            { name: 'Amit Patel', role: 'Office Administrator', review: 'Excellent quality and fast delivery! The storage cabinets keep our office organized and look professional. Highly recommend!', category: 'Storage Cabinets' },
            { name: 'Sneha Reddy', role: 'HR Director', review: 'The modular design is brilliant! We reconfigured our entire floor in just 2 hours. No other furniture offers this flexibility.', category: 'Modular Workstations' },
            { name: 'Vikram Desai', role: 'Finance Head', review: 'Great value for money! The bulk discount saved us a lot. Installation team was professional and furniture quality exceeded expectations.', category: 'Modular Workstations' }
          ].map((item, i) => (
            <div key={i} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, j) => (
                  <svg key={j} className="w-5 h-5 fill-primary" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
              <p className="text-sm text-foreground mb-4 leading-relaxed">
                "{item.review}"
              </p>
              <p className="font-bold text-foreground">{item.name}</p>
              <p className="text-xs text-muted-foreground mb-2">{item.role}</p>
              <span className="text-xs font-semibold text-primary">{item.category}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CTASection() {
  return (
    <section className="py-24 bg-gradient-to-r from-primary via-accent to-foreground text-white">
      <div className="container mx-auto px-8 text-center">
        <h2 className="text-4xl md:text-6xl font-black uppercase mb-6">
          READY TO TRANSFORM YOUR OFFICE?
        </h2>
        <p className="text-xl mb-10 max-w-2xl mx-auto opacity-90">
          Get in touch with our team to discuss your workspace needs
        </p>
        <Link to="/contact">
          <Button className="bg-white text-foreground hover:bg-white/90 rounded-full uppercase tracking-wider font-bold px-12 py-7 text-lg shadow-2xl hover:scale-105 transition-all duration-300">
            CONTACT US TODAY
            <ArrowRight className="ml-2 w-6 h-6" />
          </Button>
        </Link>
      </div>
    </section>
  );
}

export function SEOContentSection() {
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-8 max-w-6xl">
        <h2 className="text-3xl md:text-4xl font-black uppercase mb-8 text-center">
          Buy Office Furniture Online - Modular Workstations & Privacy Pods
        </h2>
        
        <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
          <p>
            Welcome to India's premier <strong>office furniture store</strong> offering <strong>readymade office furniture</strong> solutions. <strong>Buy office furniture online</strong> from our extensive collection of <strong>modular office workstations</strong>, <strong>office privacy pods</strong>, and <strong>office storage cabinets</strong> designed for modern workspaces.
          </p>
          
          <h3 className="text-2xl font-bold text-foreground mt-8 mb-4">Buy Modular Office Workstations Online</h3>
          <p>
            <strong>Buy modular workstation online</strong> from our collection of <strong>modern office workstations</strong>. Our <strong>office furniture modular workstations</strong> include <strong>ready modular workstation</strong> and <strong>readymade modular workstation</strong> options. Shop <strong>workstations for office</strong> with ergonomic designs perfect for collaborative spaces. Each <strong>office workstation</strong> is ready to deliver in Mumbai & Pan India.
          </p>
          
          <h3 className="text-2xl font-bold text-foreground mt-8 mb-4">Office Privacy Pods & Soundproof Meeting Booths</h3>
          <p>
            <strong>Buy office privacy pod</strong> solutions including <strong>soundproof office meeting pods</strong> and <strong>phone booths for office</strong>. Our <strong>office privacy pods</strong> provide perfect acoustic isolation for calls and focused work. <strong>Buy phone booth online</strong> or choose from our <strong>ready phone booth for office</strong> options - all soundproof and ready to install.
          </p>
          
          <h3 className="text-2xl font-bold text-foreground mt-8 mb-4">Office Storage Solutions & Cabinets</h3>
          <p>
            Shop our <strong>office storage solutions</strong> including <strong>office storage cabinets</strong>, <strong>office cabinets</strong>, and <strong>modular office storage</strong>. <strong>Buy office cabinet</strong> with drawers from our range of <strong>office storage cabinets with drawers</strong> designed for maximum organization and efficiency.
          </p>
          
          <h3 className="text-2xl font-bold text-foreground mt-8 mb-4">Ready to Deliver Office Furniture in Mumbai & Pan India</h3>
          <p>
            We specialize in <strong>ready to deliver office furniture</strong> and <strong>buy readymade office furniture</strong> solutions. Our <strong>ecommerce modular furniture</strong> platform makes it easy to <strong>buy office furniture online</strong>. Visit our <strong>office furniture store</strong> or browse our complete range of <strong>office furniture shops</strong> online. We offer <strong>office solutions furniture</strong> for businesses of all sizes across Mumbai & Pan India.
          </p>
          
          <div className="bg-primary/10 p-6 rounded-lg mt-8">
            <p className="font-bold text-foreground mb-2">Why Choose Our Office Furniture Store?</p>
            <ul className="space-y-2 text-sm">
              <li>✓ <strong>Buy readymade office furniture</strong> - Ready to deliver instantly</li>
              <li>✓ <strong>Modern office workstations</strong> - Latest designs</li>
              <li>✓ <strong>Soundproof meeting pods</strong> - Premium acoustic solutions</li>
              <li>✓ <strong>Office storage cabinets</strong> - Smart organization solutions</li>
              <li>✓ Pan India delivery available</li>
              <li>✓ Easy online ordering - <strong>Buy office furniture online</strong> 24/7</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
